"use client";

import { Check } from "lucide-react";

import { Button } from "@/components/ui/button";

interface PaymentSuccessViewProps {
  title: string;
  body: string;
  ctaLabel: string;
  onContinue: () => void;
  /** Optional muted line shown when an auto-redirect is pending. */
  redirectingLabel?: string;
}

/**
 * Confirmation state shown after a payment is captured. Replaces an abrupt
 * redirect with a clear success moment: an animated check, what was unlocked,
 * and an explicit "continue" control (auto-redirect is only a fallback).
 */
export function PaymentSuccessView({
  title,
  body,
  ctaLabel,
  onContinue,
  redirectingLabel,
}: PaymentSuccessViewProps) {
  return (
    <div
      className="flex flex-col items-center gap-4 py-4 text-center"
      role="status"
      aria-live="polite"
    >
      <span className="relative flex size-16 items-center justify-center">
        <span
          className="absolute inset-0 rounded-full bg-status-success-text/20 motion-safe:animate-ping"
          aria-hidden="true"
        />
        <span className="relative flex size-16 items-center justify-center rounded-full bg-status-success-text text-white shadow-card motion-safe:animate-in motion-safe:zoom-in-50 motion-safe:duration-300">
          <Check className="size-8" strokeWidth={2.5} aria-hidden="true" />
        </span>
      </span>

      <div className="space-y-1.5">
        <h3 className="text-xl font-bold text-text-heading">{title}</h3>
        <p className="mx-auto max-w-sm text-sm leading-6 text-text-muted">
          {body}
        </p>
      </div>

      <Button
        type="button"
        onClick={onContinue}
        className="mt-1 min-h-12 w-full bg-brand-primary text-base font-semibold text-white hover:bg-brand-primary-hover"
      >
        {ctaLabel}
      </Button>

      {redirectingLabel ? (
        <p className="text-xs text-text-muted">{redirectingLabel}</p>
      ) : null}
    </div>
  );
}
